import React, { useState, useEffect, useCallback } from "react";
import { Container, Card, Button, Modal, Form, Badge, Spinner, Row, Col, Tabs, Tab, ListGroup } from "react-bootstrap";
import { FaEdit, FaTrash, FaLock, FaLockOpen, FaPlus } from "react-icons/fa";

import UserService from "../services/user.service";
import EventBus from "../common/EventBus";
import ErrorModal from "./ErrorModal";
import BoardServices from "./BoardServices";

// Leeres Formular fuer einen Showcase-Container (Produktart).
const emptyForm = {
  title: "",
  description: "",
  ad_text: "",
  ad_image_url: "",
  ad_media_url: "",
  max_quantity: "",
  main_vo_ids: [],
};

const BoardProduction = () => {
  const [showcases, setShowcases] = useState([]);
  const [reloadFlag, setReloadFlag] = useState(false);
  // Erhoeht sich bei jeder Produktzuordnung, damit alle Produkttabellen neu laden.
  const [serviceReload, setServiceReload] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleServiceMutate = useCallback(() => {
    setServiceReload((p) => p + 1);
  }, []);

  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("allgemein");
  const [mainVos, setMainVos] = useState([]);

  const [showError, setShowError] = useState(false);
  const [apiError, setApiError] = useState("");
  const [apiErrorCode, setApiErrorCode] = useState("");

  const handleApiError = useCallback((error, fallback) => {
    const msg =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      fallback;
    setApiError(`${fallback}: ${msg}`);
    setApiErrorCode(error.code || (error.response && error.response.status) || "");
    setShowError(true);
    if (error.response && error.response.status === 401) {
      EventBus.dispatch("logout");
    }
  }, []);

  useEffect(() => {
    UserService.getShowcaseBoard().then(
      (response) => {
        setShowcases(Array.isArray(response.data) ? response.data : []);
      },
      (error) => {
        setShowcases([]);
        handleApiError(error, "Fehler beim Laden der Produktarten");
      }
    );
  }, [reloadFlag, handleApiError]);

  useEffect(() => {
    UserService.getMainVosBoard().then(
      (response) => {
        setMainVos(Array.isArray(response.data) ? response.data : []);
      },
      (error) => {
        setMainVos([]);
        handleApiError(error, "Fehler beim Laden der MainVOs");
      }
    );
  }, [handleApiError]);

  const openCreate = () => {
    setIsEdit(false);
    setEditId(null);
    setFormData({ ...emptyForm, main_vo_ids: [] });
    setErrors({});
    setActiveTab("allgemein");
    setShowModal(true);
  };

  const openEdit = (showcase) => {
    setIsEdit(true);
    setEditId(showcase.showcase_id);
    setFormData({
      title: showcase.title || "",
      description: showcase.description || "",
      ad_text: showcase.ad_text || "",
      ad_image_url: showcase.ad_image_url || "",
      ad_media_url: showcase.ad_media_url || "",
      max_quantity: showcase.max_quantity ?? "",
      main_vo_ids: Array.isArray(showcase.main_vo_ids) ? showcase.main_vo_ids : [],
    });
    setErrors({});
    setActiveTab("allgemein");
    setShowModal(true);
  };

  const toggleMainVo = (id) => {
    setFormData((prev) => {
      const set = new Set(prev.main_vo_ids || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, main_vo_ids: Array.from(set) };
    });
  };

  const validate = () => {
    const e = {};
    if (!formData.title || !formData.title.trim()) {
      e.title = "Titel ist erforderlich.";
    }
    const urlRe = /^https?:\/\/[^\s]+$/i;
    if (formData.ad_image_url && formData.ad_image_url.trim() && !urlRe.test(formData.ad_image_url.trim())) {
      e.ad_image_url = "Bitte eine gueltige URL (http/https) angeben.";
    }
    if (formData.ad_media_url && formData.ad_media_url.trim() && !urlRe.test(formData.ad_media_url.trim())) {
      e.ad_media_url = "Bitte eine gueltige URL (http/https) angeben.";
    }
    if (formData.max_quantity !== "" && formData.max_quantity !== null && formData.max_quantity !== undefined) {
      const n = Number(formData.max_quantity);
      if (!Number.isInteger(n) || n < 0) {
        e.max_quantity = "Bitte eine ganze Zahl (>= 0) angeben.";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      // Bei Validierungsfehlern zum betroffenen Tab springen.
      const n = Number(formData.max_quantity);
      const maxInvalid =
        formData.max_quantity !== "" &&
        formData.max_quantity != null &&
        (!Number.isInteger(n) || n < 0);
      setActiveTab(maxInvalid ? "einstellungen" : "allgemein");
      return;
    }
    try {
      setLoading(true);
      if (isEdit) {
        await UserService.updateShowcase(editId, formData);
      } else {
        await UserService.createShowcase(formData);
      }
      setShowModal(false);
      setReloadFlag((p) => !p);
    } catch (error) {
      handleApiError(error, isEdit ? "Fehler beim Aktualisieren" : "Fehler beim Anlegen");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLock = async (showcase) => {
    const nextStatus = showcase.status === "LOCKED" ? "ACTIVE" : "LOCKED";
    try {
      setLoading(true);
      await UserService.updateShowcase(showcase.showcase_id, { status: nextStatus });
      setReloadFlag((p) => !p);
    } catch (error) {
      handleApiError(error, "Fehler beim Sperren/Entsperren");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (showcase) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Produktart "${showcase.title}" wirklich loeschen? Zugeordnete Produkte bleiben erhalten (ohne Zuordnung).`)) {
      return;
    }
    try {
      setLoading(true);
      await UserService.deleteShowcase(showcase.showcase_id);
      setReloadFlag((p) => !p);
    } catch (error) {
      handleApiError(error, "Fehler beim Loeschen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="mt-4">
      <ErrorModal
        show={showError}
        handleClose={() => setShowError(false)}
        errorCode={apiErrorCode}
        errorMessage={apiError}
      />

      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <h1 className="mb-0">Produktion</h1>
        <Button variant="outline-primary" onClick={openCreate}>
          <FaPlus className="me-2" /> Neue Produktart hinzufügen
        </Button>
      </div>

      {loading && (
        <div className="spinner-overlay">
          <div className="spinner-background">
            <Spinner animation="border" role="status" className="spinner-border">
              <span className="sr-only">Lade...</span>
            </Spinner>
            <div className="spinner-text">... bitte warten ...</div>
          </div>
        </div>
      )}

      {showcases.length === 0 && (
        <Card className="text-center text-muted p-5">
          <Card.Body>
            Noch keine Produktart angelegt. Klicken Sie auf
            <strong> „Neue Produktart hinzufügen" </strong>
            (z. B. „Google Review"), um zu starten.
          </Card.Body>
        </Card>
      )}

      {showcases.map((s) => (
        <Card key={s.showcase_id} className="mb-4 shadow-sm">
          <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center gap-2">
              <h4 className="mb-0">{s.title}</h4>
              {s.status === "LOCKED" ? (
                <Badge bg="secondary">Gesperrt</Badge>
              ) : (
                <Badge bg="success">Aktiv</Badge>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button size="sm" variant="warning" onClick={() => openEdit(s)} title="Bearbeiten">
                <FaEdit />
              </Button>
              <Button
                size="sm"
                variant={s.status === "LOCKED" ? "secondary" : "outline-secondary"}
                onClick={() => handleToggleLock(s)}
                title={s.status === "LOCKED" ? "Entsperren" : "Sperren"}
              >
                {s.status === "LOCKED" ? <FaLockOpen /> : <FaLock />}
              </Button>
              <Button size="sm" variant="danger" onClick={() => handleDelete(s)} title="Löschen">
                <FaTrash />
              </Button>
            </div>
          </Card.Header>

          <Card.Body>
            <Row className="g-3 mb-2">
              <Col xs={12} md={s.ad_image_url ? 8 : 12}>
                {s.description && <p className="mb-2">{s.description}</p>}
                {s.ad_text && (
                  <div className="p-3 mb-2 bg-light rounded">
                    <strong>Werbung:</strong>
                    <div>{s.ad_text}</div>
                    {s.ad_media_url && (
                      <a href={s.ad_media_url} target="_blank" rel="noreferrer">
                        Mehr ansehen
                      </a>
                    )}
                  </div>
                )}
              </Col>
              {s.ad_image_url && (
                <Col xs={12} md={4} className="text-center">
                  <img
                    src={s.ad_image_url}
                    alt={`Werbung ${s.title}`}
                    style={{ maxWidth: "100%", maxHeight: 160, objectFit: "contain" }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </Col>
              )}
            </Row>

            {/* Bestehende Produkttabelle - unveraendert, nur auf diesen Container gefiltert */}
            <div className="border-top pt-2">
              <BoardServices
                showcaseId={s.showcase_id}
                title="Produkte"
                reloadSignal={serviceReload}
                onMutate={handleServiceMutate}
              />
            </div>
          </Card.Body>
        </Card>
      ))}

      {/* Nicht zugeordnete Produkte: bestehende Tabelle unveraendert, nur Produkte ohne Container */}
      <Card className="mb-4 border-secondary">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Nicht zugeordnete Produkte</h5>
          <small className="text-muted">
            Bestehende Produkte ohne Produktart-Zuordnung. Neue Produkte legen Sie direkt in einer Produktart oben an.
          </small>
        </Card.Header>
        <Card.Body>
          <BoardServices
            unassignedOnly
            title="Nicht zugeordnete Produkte"
            showcases={showcases}
            allowCreate={false}
            reloadSignal={serviceReload}
            onMutate={handleServiceMutate}
          />
        </Card.Body>
      </Card>

      {/* Modal: Produktart anlegen/bearbeiten */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? "Produktart bearbeiten" : "Neue Produktart"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k || "allgemein")}
              className="mb-3"
            >
              <Tab eventKey="allgemein" title="Allgemein">
                <Form.Group className="mb-3">
                  <Form.Label>Titel *</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    placeholder="z. B. Google Review"
                    value={formData.title}
                    onChange={handleChange}
                    isInvalid={!!errors.title}
                  />
                  <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Beschreibung</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    placeholder="Kurzbeschreibung der Produktart"
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Werbung (Text)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="ad_text"
                    placeholder="Werbe-/Marketingtext (wird als Klartext angezeigt)"
                    value={formData.ad_text}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Werbung – Bild-URL</Form.Label>
                  <Form.Control
                    type="text"
                    name="ad_image_url"
                    placeholder="https://..."
                    value={formData.ad_image_url}
                    onChange={handleChange}
                    isInvalid={!!errors.ad_image_url}
                  />
                  <Form.Control.Feedback type="invalid">{errors.ad_image_url}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Werbung – Video-/Link-URL (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="ad_media_url"
                    placeholder="https://..."
                    value={formData.ad_media_url}
                    onChange={handleChange}
                    isInvalid={!!errors.ad_media_url}
                  />
                  <Form.Control.Feedback type="invalid">{errors.ad_media_url}</Form.Control.Feedback>
                </Form.Group>
              </Tab>

              <Tab eventKey="einstellungen" title="Einstellungen">
                <Form.Group className="mb-3">
                  <Form.Label>Menge Max.</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    name="max_quantity"
                    placeholder="z. B. 1000"
                    value={formData.max_quantity}
                    onChange={handleChange}
                    isInvalid={!!errors.max_quantity}
                  />
                  <Form.Control.Feedback type="invalid">{errors.max_quantity}</Form.Control.Feedback>
                  <Form.Text muted>
                    Maximale Gesamt-Stückzahl dieser Produktart. Leer = unbegrenzt.
                  </Form.Text>
                </Form.Group>
              </Tab>

              <Tab eventKey="vertrieb" title="Vertrieb">
                <p className="text-muted">
                  Hier ordnen Sie die Produktart einem oder mehreren MainVOs zu.
                </p>
                {mainVos.length === 0 ? (
                  <p className="text-muted">Keine MainVOs vorhanden.</p>
                ) : (
                  <ListGroup style={{ maxHeight: 300, overflowY: "auto" }}>
                    {mainVos.map((m) => (
                      <ListGroup.Item key={m.main_vo_id}>
                        <Form.Check
                          type="checkbox"
                          id={`mainvo-${m.main_vo_id}`}
                          label={m.company_name || `MainVO #${m.main_vo_id}`}
                          checked={(formData.main_vo_ids || []).includes(m.main_vo_id)}
                          onChange={() => toggleMainVo(m.main_vo_id)}
                        />
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Tab>
            </Tabs>

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>
                Abbrechen
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? "Wird gespeichert..." : isEdit ? "Speichern" : "Anlegen"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default BoardProduction;
